import axios from 'axios';

// Use relative URL so the same build works on any domain
const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A late 401 from an old session must not erase a newer D2L sign-in.
    // The launch/profile flows render their own errors instead of visiting
    // the staff login page (another navigation LockDown Browser may block).
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect
      && error.config?.headers?.Authorization === `Bearer ${localStorage.getItem('token')}`) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const auth = {
  getProfile: (token) => api.get('/auth/me', {
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    skipAuthRedirect: true, timeout: 20000,
  }).then(res => res.data),
  register: (data) => api.post('/auth/register', data).then(res => res.data),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { currentPassword, newPassword }).then(res => res.data),
  logout: () => api.post('/auth/logout').then(res => res.data),
  // OTP Authentication (for students)
  requestOTP: (studentId, labId) => 
    api.post('/auth/otp/request', { studentId, labId }).then(res => res.data),
  verifyOTP: (studentId, code) => 
    api.post('/auth/otp/verify', { studentId, code }).then(res => res.data),
  // OTP Authentication (for faculty/admin)
  requestAdminOTP: (email) => 
    api.post('/auth/otp/admin/request', { email }).then(res => res.data),
  verifyAdminOTP: (email, code) => 
    api.post('/auth/otp/admin/verify', { email, code }).then(res => res.data),
  // Admin only
  listUsers: () => api.get('/auth/users').then(res => res.data),
  createUser: (data) => api.post('/auth/users', data).then(res => res.data),
  updateUser: (id, data) => api.put(`/auth/users/${id}`, data).then(res => res.data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`).then(res => res.data),
};

// Users (admin-only staff management — faculty/admin CRUD; students read-only)
export const users = {
  list: (params = {}) => api.get('/users', { params }).then(res => res.data),
  create: (data) => api.post('/users', data).then(res => res.data),
  get: (id) => api.get(`/users/${id}`).then(res => res.data),
  update: (id, data) => api.patch(`/users/${id}`, data).then(res => res.data),
  setPassword: (id, data) => api.post(`/users/${id}/set-password`, data).then(res => res.data),
  remove: (id) => api.delete(`/users/${id}`).then(res => res.data),
};

// Labs
const OPERATION_ACTIVE = ['running', 'cancelling'];

async function containerOperation(id, action, body = {}, { onProgress } = {}) {
  const response = await api.post(`/labs/${id}/${action}`, { ...body, async: true });
  if (!response.data.operation) return response.data; // Compatibility with older backends.
  const operationId = response.data.operation.id;
  let operation = response.data.operation;
  onProgress?.(operation);
  while (OPERATION_ACTIVE.includes(operation.status)) {
    await new Promise(resolve => setTimeout(resolve, 1500));
    const latest = (await api.get(`/labs/${id}/container-operation`)).data.operation;
    if (!latest || latest.id !== operationId) {
      throw new Error('A newer container operation replaced this one. Check the live progress.');
    }
    operation = latest;
    onProgress?.(operation);
  }
  if (operation.status !== 'completed') throw new Error(operation.error || 'Container operation was cancelled');
  return operation.result;
}

/** Wait (up to ~15 minutes) for a lab's other operation to finish. */
async function waitForOperation(id, operationId) {
  const deadline = Date.now() + 15 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise(resolve => setTimeout(resolve, 3000));
    const latest = (await api.get(`/labs/${id}/container-operation`)).data.operation;
    if (!latest || latest.id !== operationId || !OPERATION_ACTIVE.includes(latest.status)) return;
  }
  throw new Error('Another operation on this lab is taking too long. Try again later.');
}

/**
 * Enrol students in a lab and create their environments. A lab runs one
 * operation at a time: if Start all or another add is under way, wait for it
 * and try again rather than failing.
 */
async function createEnvironments(id, studentIds, { onProgress, onWaiting } = {}) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await containerOperation(id, 'environments', { studentIds }, { onProgress });
    } catch (error) {
      const other = error.response?.status === 409 ? error.response.data?.operation : null;
      if (!other || attempt >= 2) throw error;
      onWaiting?.(other);
      await waitForOperation(id, other.id);
    }
  }
}

export const labs = {
  list: () => api.get('/labs').then(res => res.data),
  get: (id) => api.get(`/labs/${id}`).then(res => res.data),
  create: (data) => api.post('/labs', data).then(res => res.data),
  update: (id, data) => api.put(`/labs/${id}`, data).then(res => res.data),
  delete: (id, options = {}) => api.delete(`/labs/${id}`, { params: options }).then(res => res.data),
  open: (id) => api.post(`/labs/${id}/open`).then(res => res.data),
  close: (id) => api.post(`/labs/${id}/close`).then(res => res.data),
  start: (id) => containerOperation(id, 'start'),
  stop: (id) => containerOperation(id, 'stop'),
  recreateAll: (id) => containerOperation(id, 'recreate-all'),
  createEnvironments,
  containerOperation: (id) => api.get(`/labs/${id}/container-operation`).then(res => res.data),
  authenticateStudentIDE: (id, studentId) => api.post(`/labs/${id}/students/${studentId}/authenticate`, {}, { withCredentials: true }).then(res => res.data),
  // Starter code - upload to a specific lab
  uploadStarterCodeToLab: (labId, file) => {
    const formData = new FormData();
    formData.append('starterCode', file);
    return api.post(`/labs/${labId}/upload-starter`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },
  getStarterFiles: (labId) => api.get(`/labs/${labId}/starter-files`).then(res => res.data),
  getStarterFile: (labId, filePath) => api.get(`/labs/${labId}/starter-files/${encodeURIComponent(filePath)}`).then(res => res.data),
  updateStarterFile: (labId, filePath, content) => api.put(`/labs/${labId}/starter-files/${encodeURIComponent(filePath)}`, { content }).then(res => res.data),
  createStarterFile: (labId, filePath, content = '') => api.post(`/labs/${labId}/starter-files`, { path: filePath, content }).then(res => res.data),
  deleteStarterFile: (labId, filePath) => api.delete(`/labs/${labId}/starter-files/${encodeURIComponent(filePath)}`).then(res => res.data),
  renameStarterFile: (labId, filePath, newPath) => api.patch(`/labs/${labId}/starter-files/${encodeURIComponent(filePath)}`, { newPath }).then(res => res.data),
  copyStarterFile: (labId, sourcePath, destinationPath) => api.post(`/labs/${labId}/starter-files/copy`, { sourcePath, destinationPath }).then(res => res.data),
  downloadStarterCode: (labId) => api.get(`/labs/${labId}/starter-code/download`, { responseType: 'blob' }),
  reloadStarterCodeForStudent: (labId, studentId) => api.post(`/students/lab/${labId}/${studentId}/reload-starter`).then(res => res.data),
  // Bulk-create containers for already-enrolled students (used after creating a lab from a
  // section, where the backend enrolls the roster but does NOT create containers). Reuses the
  // per-student create endpoint; 409 (already exists) counts as success; one retry on transient
  // failure (e.g. a 504 while the pod warms up — the retry then hits 409 and resolves).
  createContainersBulk: async (labId, students, { parallelLimit = 8, onProgress } = {}) => {
    const results = { success: [], failed: [] };
    const list = (students || []).filter(Boolean);
    if (list.length === 0) return results;
    const total = list.length;
    let done = 0;
    for (let i = 0; i < list.length; i += parallelLimit) {
      const chunk = list.slice(i, i + parallelLimit);
      await Promise.all(chunk.map(async (s) => {
        const sid = s.student_id || s.studentId || s;
        let lastErr = null;
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            await api.post(`/students/lab/${labId}/${sid}/container`, {
              name: s.student_name || s.name,
              email: s.student_email || s.email,
            });
            lastErr = null;
            break;
          } catch (err) {
            if (err.response?.status === 409) { lastErr = null; break; } // already exists
            lastErr = err;
          }
        }
        if (lastErr) results.failed.push({ studentId: sid, error: lastErr.response?.data?.error || lastErr.message });
        else results.success.push(sid);
        done += 1;
        if (onProgress) {
          onProgress({
            stage: done >= total ? 'complete' : 'creating',
            current: done,
            total,
            successCount: results.success.length,
            failedCount: results.failed.length,
            message: `Creating containers: ${done}/${total}${results.failed.length ? ` (${results.failed.length} failed)` : ''}`,
          });
        }
      }));
    }
    return results;
  },
  reloadStarterCode: (labId) => api.post(`/labs/${labId}/reload-starter`).then(res => res.data),
  // Bulk reload starter code with parallel processing and progress tracking
  reloadStarterCodeBulk: async (labId, data) => {
    const { studentIds, parallelLimit = 50, onProgress } = data;
    const results = { success: [], failed: [] };
    
    if (!studentIds || studentIds.length === 0) {
      throw new Error('No students provided');
    }
    
    const chunks = [];
    for (let i = 0; i < studentIds.length; i += parallelLimit) {
      chunks.push(studentIds.slice(i, i + parallelLimit));
    }
    
    let totalProcessed = 0;
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      if (onProgress) {
        onProgress({ 
          stage: 'reloading', 
          current: totalProcessed, 
          total: studentIds.length, 
          message: `Reloading starter code: ${totalProcessed}/${studentIds.length} (batch ${chunkIndex + 1}/${chunks.length})` 
        });
      }
      
      const promises = chunk.map(async (studentId) => {
        try {
          await api.post(`/students/lab/${labId}/${studentId}/reload-starter`);
          results.success.push(studentId);
          totalProcessed++;
          if (onProgress) {
            onProgress({ 
              stage: 'reloading', 
              current: totalProcessed, 
              total: studentIds.length, 
              message: `Reloading starter code: ${totalProcessed}/${studentIds.length}` 
            });
          }
        } catch (err) {
          results.failed.push({ studentId, error: err.message });
          totalProcessed++;
          if (onProgress) {
            onProgress({ 
              stage: 'reloading', 
              current: totalProcessed, 
              total: studentIds.length, 
              message: `Reloading starter code: ${totalProcessed}/${studentIds.length} (${results.failed.length} failed)` 
            });
          }
        }
      });
      await Promise.all(promises);
    }
    
    if (onProgress) {
      onProgress({ 
        stage: 'complete', 
        current: totalProcessed, 
        total: studentIds.length, 
        successCount: results.success.length,
        failedCount: results.failed.length,
        message: `Complete: ${results.success.length} successful, ${results.failed.length} failed` 
      });
    }
    
    return results;
  },
  // Clear ONE student's workspace. The bulk helper below is a loop over this
  // same endpoint, which has existed since the beginning -- only the UI to
  // reach a single student was missing.
  clearStarterCodeForStudent: (labId, studentId) =>
    api.post(`/students/lab/${labId}/${studentId}/clear-starter`).then((res) => res.data),

  // Bulk clear starter code (workspace files) with parallel processing and progress tracking
  clearStarterCodeBulk: async (labId, data) => {
    const { studentIds, parallelLimit = 50, onProgress } = data;
    const results = { success: [], failed: [] };

    if (!studentIds || studentIds.length === 0) {
      throw new Error('No students provided');
    }

    const chunks = [];
    for (let i = 0; i < studentIds.length; i += parallelLimit) {
      chunks.push(studentIds.slice(i, i + parallelLimit));
    }

    let totalProcessed = 0;
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      if (onProgress) {
        onProgress({
          stage: 'clearing',
          current: totalProcessed,
          total: studentIds.length,
          message: `Clearing workspaces: ${totalProcessed}/${studentIds.length} (batch ${chunkIndex + 1}/${chunks.length})`
        });
      }

      const promises = chunk.map(async (studentId) => {
        try {
          await api.post(`/students/lab/${labId}/${studentId}/clear-starter`);
          results.success.push(studentId);
          totalProcessed++;
          if (onProgress) {
            onProgress({
              stage: 'clearing',
              current: totalProcessed,
              total: studentIds.length,
              message: `Clearing workspaces: ${totalProcessed}/${studentIds.length}`
            });
          }
        } catch (err) {
          results.failed.push({ studentId, error: err.message });
          totalProcessed++;
          if (onProgress) {
            onProgress({
              stage: 'clearing',
              current: totalProcessed,
              total: studentIds.length,
              message: `Clearing workspaces: ${totalProcessed}/${studentIds.length} (${results.failed.length} failed)`
            });
          }
        }
      });
      await Promise.all(promises);
    }

    if (onProgress) {
      onProgress({
        stage: 'complete',
        current: totalProcessed,
        total: studentIds.length,
        successCount: results.success.length,
        failedCount: results.failed.length,
        message: `Complete: ${results.success.length} cleared, ${results.failed.length} failed`
      });
    }

    return results;
  },
  // Student enrollment
  enroll: (labId, studentIds) => api.post(`/labs/${labId}/enroll`, { studentIds }).then(res => res.data),
  removeStudent: (labId, studentId) => api.delete(`/labs/${labId}/students/${studentId}`).then(res => res.data),
  removeAllStudents: (labId, options = {}) => api.delete(`/labs/${labId}/students`, { params: options }).then(res => res.data),
};

// Students & Containers
export const students = {
  add: (labId, data) => {
    // Enroll student (create if doesn't exist)
    return api.post(`/labs/${labId}/enroll`, { 
      studentIds: [data.studentId],
      name: data.name,
      email: data.email
    }).then(async (res) => {
      // If container creation requested, create it after a small delay
      if (data.createContainer) {
        try {
          // Wait a bit for student to be fully created
          await new Promise(resolve => setTimeout(resolve, 500));
          // Pass name and email so container route can auto-create student if needed
          const containerRes = await api.post(`/students/lab/${labId}/${data.studentId}/container`, {
            name: data.name,
            email: data.email
          });
          console.log('Container created:', containerRes.data);
        } catch (err) {
          console.error('Container creation failed:', err);
          // Don't throw - enrollment succeeded
        }
      }
      return res.data;
    });
  },
  remove: (labId, studentId, options = {}) => 
    api.delete(`/labs/${labId}/students/${studentId}`, { params: options }).then(res => res.data),
  createContainer: (labId, studentId, data) => 
    api.post(`/students/lab/${labId}/${studentId}/container`, data).then(res => res.data),
  deleteContainer: (labId, studentId) => 
    api.delete(`/students/lab/${labId}/${studentId}/container`).then(res => res.data),
  start: (labId, studentId) =>
    api.post(`/students/lab/${labId}/${studentId}/container/start`).then(res => res.data),
  stop: (labId, studentId) =>
    api.post(`/students/lab/${labId}/${studentId}/container/stop`).then(res => res.data),
  reloadStarterCode: (labId, studentId) => 
    api.post(`/students/lab/${labId}/${studentId}/reload-starter`).then(res => res.data),
  // Bulk add students with optional container creation
  addBulk: async (labId, data) => {
    const { students: studentList, createContainers, parallelLimit = 50, onProgress } = data;
    const results = { enrolled: [], failed: [], containersCreated: [] };
    
    // First, enroll all students
    const studentIds = studentList.map(s => s.studentId);
    try {
      if (onProgress) onProgress({ stage: 'enrolling', current: 0, total: studentList.length, message: 'Enrolling students...' });
      const enrollRes = await api.post(`/labs/${labId}/enroll-bulk`, { 
        students: studentList 
      });
      results.enrolled = enrollRes.data.results?.enrolled || studentIds;
      if (onProgress) onProgress({ stage: 'enrolled', current: results.enrolled.length, total: studentList.length, message: `Enrolled ${results.enrolled.length} students` });
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to enroll students');
    }
    
    // Then create containers if requested (in parallel with limit)
    if (createContainers) {
      const chunks = [];
      for (let i = 0; i < studentList.length; i += parallelLimit) {
        chunks.push(studentList.slice(i, i + parallelLimit));
      }
      
      let totalProcessed = 0;
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        if (onProgress) {
          onProgress({ 
            stage: 'containers', 
            current: totalProcessed, 
            total: studentList.length, 
            message: `Creating containers: ${totalProcessed}/${studentList.length} (batch ${chunkIndex + 1}/${chunks.length})` 
          });
        }
        
        const promises = chunk.map(async (student) => {
          try {
            await api.post(`/students/lab/${labId}/${student.studentId}/container`, {
              name: student.name,
              email: student.email
            });
            results.containersCreated.push(student.studentId);
            totalProcessed++;
            if (onProgress) {
              onProgress({ 
                stage: 'containers', 
                current: totalProcessed, 
                total: studentList.length, 
                message: `Creating containers: ${totalProcessed}/${studentList.length}` 
              });
            }
          } catch (err) {
            results.failed.push({ studentId: student.studentId, error: err.message });
            totalProcessed++;
            if (onProgress) {
              onProgress({ 
                stage: 'containers', 
                current: totalProcessed, 
                total: studentList.length, 
                message: `Creating containers: ${totalProcessed}/${studentList.length} (${results.failed.length} failed)` 
              });
            }
          }
        });
        await Promise.all(promises);
      }
      
      if (onProgress) {
        onProgress({ 
          stage: 'complete', 
          current: totalProcessed, 
          total: studentList.length, 
          message: `Complete: ${results.containersCreated.length} containers created, ${results.failed.length} failed` 
        });
      }
    }
    
    return results;
  },
};

// Sections (Reusable Student Rosters)
export const sections = {
  list: (params = {}) => api.get('/sections', { params }).then(res => res.data),
  get: (id) => api.get(`/sections/${id}`).then(res => res.data),
  create: (data) => api.post('/sections', data).then(res => res.data),
  update: (id, data) => api.put(`/sections/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/sections/${id}`).then(res => res.data),
  addStudents: (id, data) => api.post(`/sections/${id}/students`, data).then(res => res.data),
  removeStudent: (id, studentUserId) => api.delete(`/sections/${id}/students/${studentUserId}`).then(res => res.data),
  syncToLabs: (id) => api.post(`/sections/${id}/sync-to-labs`).then(res => res.data),
  // The labs this section was used for, and which students lack an environment in each.
  labs: (id, studentIds) => api.get(`/sections/${id}/labs`, {
    params: studentIds?.length ? { students: studentIds.join(',') } : {},
  }).then(res => res.data),
};

// Containers
export const containers = {
  list: (params = {}) => api.get('/containers', { params }).then(res => res.data),
  stats: () => api.get('/containers/stats', { timeout: 20000 }).then(res => res.data),
  get: (id) => api.get(`/containers/${id}`).then(res => res.data),
  start: (id) => api.post(`/containers/${id}/start`).then(res => res.data),
  stop: (id) => api.post(`/containers/${id}/stop`).then(res => res.data),
  delete: (id) => api.delete(`/containers/${id}`).then(res => res.data),
  bulkDelete: (data) => api.post('/containers/bulk-delete', data).then(res => res.data),
};

// Lab environment catalogue — the closed set of container images a lab may run
// (backend: models/LabImage + services/LabImageCatalogue + routes/lab-images).
//
// Every path for this feature lives here and nowhere else, so a route-name
// change is a one-file fix. The catalogue is served by a backend that ships as
// its own Kubernetes deployment, so this bundle can be live before those routes
// exist — callers must treat a 404 as "no catalogue yet", not as an error worth
// showing a faculty member. See useImageCatalog() in components/ImagePicker.js.
//
// Entries are addressed by their stable slug `key`, not by _id: `key` is what a
// lab stores in containerConfig.imageKey and what the server's own lookups use.
//
//   GET  /api/lab-images?includeRetired=true  -> { images: [entry], allowedRegistries? }
//   POST /api/lab-images                      -> entry            (admin)
//   PUT  /api/lab-images/:key                 -> entry            (admin)
//   POST /api/lab-images/:key/retire          -> { success, labsStillUsing } (admin)
//   POST /api/lab-images/:key/unretire        -> { success }      (admin)
//
// Retirement is a dedicated route rather than a field on PUT: the server refuses
// to retire the platform default and reports how many labs still reference the
// entry, neither of which a generic field update expresses.
export const labImages = {
  list: (params = {}) => api.get('/lab-images', { params }).then(res => res.data),
  create: (data) => api.post('/lab-images', data).then(res => res.data),
  update: (key, data) => api.put(`/lab-images/${encodeURIComponent(key)}`, data).then(res => res.data),
  setRetired: (key, retired) => api
    .post(`/lab-images/${encodeURIComponent(key)}/${retired ? 'retire' : 'unretire'}`)
    .then(res => res.data),
};

// Providers
export const providers = {
  current: () => api.get('/providers/current').then(res => res.data),
  health: () => api.get('/providers/health').then(res => res.data),
  available: () => api.get('/providers/available').then(res => res.data),
  switch: (data) => api.post('/providers/switch', data).then(res => res.data),
};

// Activity Logs
export const activity = {
  // Get events for a student
  getEvents: (labId, studentId, params = {}) => 
    api.get(`/activity/${labId}/${studentId}/events`, { params }).then(res => res.data),
  
  // Get activity summary for a student
  getSummary: (labId, studentId) => 
    api.get(`/activity/${labId}/${studentId}/summary`).then(res => res.data),
  
  // Get timeline for a student
  getTimeline: (labId, studentId, granularity = 'minute') => 
    api.get(`/activity/${labId}/${studentId}/timeline`, { params: { granularity } }).then(res => res.data),
  
  // Get clipboard history (for integrity review)
  getClipboard: (labId, studentId) => 
    api.get(`/activity/${labId}/${studentId}/clipboard`).then(res => res.data),
  
  // Get file edit history
  getFileEdits: (labId, studentId, file = null) => 
    api.get(`/activity/${labId}/${studentId}/file-edits`, { params: file ? { file } : {} }).then(res => res.data),
  
  // Get lab-wide activity overview
  getLabOverview: (labId) => 
    api.get(`/activity/${labId}/overview`).then(res => res.data),
};

// Screenshots (Activity Rewind)
export const screenshots = {
  // Get screenshot index for a student
  getIndex: (labId, studentId) => 
    api.get(`/students/lab/${labId}/${studentId}/screenshots`).then(res => res.data),
  
  // Get specific screenshot URL (for img src) - includes auth token
  getUrl: (labId, studentId, timestamp) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/students/lab/${labId}/${studentId}/screenshots/${timestamp}?token=${encodeURIComponent(token || '')}`;
  },
  
  // Get screenshot as blob with authentication (for img src via Object URL)
  getImageBlob: async (labId, studentId, timestamp) => {
    const response = await api.get(`/students/lab/${labId}/${studentId}/screenshots/${timestamp}`, {
      responseType: 'blob'
    });
    return URL.createObjectURL(response.data);
  },
  
  // Get screenshots in time range
  getRange: (labId, studentId, start, end, limit = 100) => 
    api.get(`/students/lab/${labId}/${studentId}/screenshots/range`, { 
      params: { start, end, limit } 
    }).then(res => res.data),
};

// System info (public)
export const system = {
  getVersion: () => api.get('/version').then(res => res.data).catch(() => null),
  getHealth: () => api.get('/health').then(res => res.data).catch(() => null),
};

// Student Portal API (for student-facing pages)
export const studentPortal = {
  // Get student's enrolled labs
  getLabs: () => api.get('/student/labs').then(res => res.data),

  // Get specific lab details for student
  getLab: (labId, options = {}) => api.get(`/student/labs/${labId}`, options).then(res => res.data),

  // Get container info for student's lab
  getContainer: (labId) => api.get(`/student/labs/${labId}/container`).then(res => res.data),

  // Authenticate for IDE access - exchanges token for cookie, returns clean URL
  authenticateForIDE: (labId, sessionToken, importId, options = {}) =>
    api.post(`/student/labs/${labId}/authenticate`, { sessionToken, ...(importId ? { importId } : {}) }, { withCredentials: true, ...options })
      .then(res => res.data),

  // Open the workspace preview on its own per-pod origin. Returns { url } that
  // plants a cookie scoped to that origin and strips the token; open it in a tab.
  // Staff name whose preview to open: { studentId } or { container }.
  openPreview: (labId, target) => api.get(`/student/labs/${labId}/preview`, { params: target || undefined }).then(res => res.data),

  // A web app the student runs (images that serve apps, e.g. INFS4102), on its
  // own origin. Same { url } handoff as openPreview; staff name the target.
  openApp: (labId, port, target) => api.get(`/student/labs/${labId}/apps/${port}`, { params: target || undefined }).then(res => res.data),
  // For /open-app: an app address reached without its sign-in.
  appLink: (host, path) => api.get('/student/app-link', { params: { host, path } }).then(res => res.data),

  listImports: (labId) => api.get(`/student/labs/${labId}/workspace-imports`).then(res => res.data),
  getImport: (labId, id) => api.get(`/student/labs/${labId}/workspace-imports/${id}`).then(res => res.data),
  createImport: (labId, data) => api.post(`/student/labs/${labId}/workspace-imports`, data).then(res => res.data),
  uploadImport: (labId, id, form, options) => api.put(`/student/labs/${labId}/workspace-imports/${id}/content`, form,
    { ...options, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 }).then(res => res.data),
  commitImport: (labId, id) => api.post(`/student/labs/${labId}/workspace-imports/${id}/commit`).then(res => res.data),
  cancelImport: (labId, id) => api.delete(`/student/labs/${labId}/workspace-imports/${id}`).then(res => res.data),

  // Get student profile
  getProfile: () => api.get('/student/profile').then(res => res.data),
};

// Faculty Pod and Snapshots API
export const facultyPod = {
  // Faculty Pod Management
  create: (labId, data = {}) =>
    api.post(`/labs/${labId}/faculty-pod`, data).then(res => res.data),
  get: (labId) =>
    api.get(`/labs/${labId}/faculty-pod`).then(res => res.data),
  start: (labId) =>
    api.post(`/labs/${labId}/faculty-pod/start`).then(res => res.data),
  stop: (labId) =>
    api.post(`/labs/${labId}/faculty-pod/stop`).then(res => res.data),
  delete: (labId, deletePvc = false) =>
    api.delete(`/labs/${labId}/faculty-pod`, { params: { deletePvc } }).then(res => res.data),

  cleanupSnapshots: (labId, keepCount = 2) =>
    api.post(`/labs/${labId}/faculty-pod/cleanup-snapshots`, { keepCount }).then(res => res.data),

  // Snapshots
  triggerSnapshot: (labId) =>
    api.post(`/labs/${labId}/snapshots/trigger`).then(res => res.data),
  snapshotStudent: (labId, studentId) =>
    api.post(`/labs/${labId}/snapshots/student/${studentId}`).then(res => res.data),
  listSnapshots: (labId, params = {}) =>
    api.get(`/labs/${labId}/snapshots`, { params }).then(res => res.data),
  getStudentSnapshots: (labId, studentId) =>
    api.get(`/labs/${labId}/snapshots/${studentId}`).then(res => res.data),
  updateSnapshotConfig: (labId, config) =>
    api.put(`/labs/${labId}/snapshot-config`, config).then(res => res.data),

  // Grading
  getGradingStudents: (labId) =>
    api.get(`/labs/${labId}/grading/students`).then(res => res.data),
  submitGrade: (labId, studentId, data) =>
    api.post(`/labs/${labId}/grading/grade/${studentId}`, data).then(res => res.data),
  updateGradingStatus: (labId, studentId, data) =>
    api.put(`/labs/${labId}/grading/status/${studentId}`, data).then(res => res.data),

  // Snapshot File Browsing
  listSnapshotTimestamps: (labId, studentId) =>
    api.get(`/labs/${labId}/grading/files/${studentId}`).then(res => res.data),
  listSnapshotFiles: (labId, studentId, timestamp, path = '') =>
    api.get(`/labs/${labId}/grading/files/${studentId}/${timestamp}/${path}`).then(res => res.data),
  getSnapshotFileContent: (labId, studentId, timestamp, path) =>
    api.get(`/labs/${labId}/grading/files/${studentId}/${timestamp}/${path}`, {
      params: { content: true }
    }).then(res => res.data),
};

export default api;


// Analytics over harvested activity rollups. Scope is always a set of labs:
// pass labId, labIds (comma-separated), courseCode and/or term.
export const analytics = {
  summary: (params) => api.get('/analytics/summary', { params }).then(r => r.data),
  students: (params) => api.get('/analytics/students', { params }).then(r => r.data),
  security: (params) => api.get('/analytics/security', { params }).then(r => r.data),
  timeline: (params) => api.get('/analytics/timeline', { params }).then(r => r.data),
  harvest: (params) => api.post('/analytics/harvest', null, { params }).then(r => r.data),
  // Drill-downs: the breakdown behind each headline number.
  labs: (params) => api.get('/analytics/labs', { params }).then(r => r.data),
  ips: (params) => api.get('/analytics/ips', { params }).then(r => r.data),
  student: (studentId, params) =>
    api.get(`/analytics/students/${encodeURIComponent(studentId)}`, { params }).then(r => r.data),
};

// Versioned, bounded analytics responses. AbortSignal comes from React Query so
// a superseded filter/search does not keep downloading an obsolete result.
export const analyticsWorkspace = {
  get: (resource, params, signal) => api.get(`/analytics/v2/${resource}`, { params, signal }).then(r => r.data),
};
